"use client";

import { Database, Download, Trash2, Upload, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocalResults } from "@/hooks/useLocalResults";
import type { OutputLanguage } from "@/types/aicheck";

interface ShareBundleControls {
  onDownload: () => void;
  downloadDisabled: boolean;
  onImportClick: () => void;
  status: string | null;
}

interface CacheManagementModalProps {
  open: boolean;
  onClose: () => void;
  currentProjectId?: number | null;
  /** 管理者のみ渡される。null/undefined のときはセクション自体を描画しない。 */
  shareBundle?: ShareBundleControls | null;
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function formatTimestamp(value: string): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

export function CacheManagementModal({
  open,
  onClose,
  currentProjectId,
  shareBundle,
}: CacheManagementModalProps) {
  const {
    getCachedProjects,
    getProjectCacheEntry,
    getRecentCacheEvictions,
    clearProjectCacheBundle,
    clearSelectedCaches,
  } = useLocalResults();
  const [projectEntries, setProjectEntries] = useState<
    ReturnType<typeof getCachedProjects>
  >([]);
  const [currentProjectEntry, setCurrentProjectEntry] =
    useState<ReturnType<typeof getProjectCacheEntry>>(null);
  const [recentEvictions, setRecentEvictions] = useState<
    ReturnType<typeof getRecentCacheEvictions>
  >([]);

  const refresh = useCallback(() => {
    setProjectEntries(getCachedProjects(currentProjectId ?? null));
    setCurrentProjectEntry(
      currentProjectId ? getProjectCacheEntry(currentProjectId) : null,
    );
    setRecentEvictions(getRecentCacheEvictions());
  }, [
    currentProjectId,
    getCachedProjects,
    getProjectCacheEntry,
    getRecentCacheEvictions,
  ]);

  useEffect(() => {
    if (!open) return;
    refresh();
  }, [open, refresh]);

  const currentProjectAvailable = useMemo(
    () => Number.isFinite(currentProjectId) && (currentProjectId ?? 0) > 0,
    [currentProjectId],
  );
  const currentProjectLanguages = useMemo(
    () => currentProjectEntry?.languages ?? [],
    [currentProjectEntry],
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] overflow-y-auto p-4">
      <button
        type="button"
        aria-label="Close cache management"
        className="fixed inset-0 bg-zinc-950/45"
        onClick={onClose}
      />
      <div className="relative z-10 flex min-h-full items-start justify-center sm:items-center">
        <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl">
          <div className="flex shrink-0 items-center justify-between border-b border-zinc-200 px-6 py-4">
            <div>
              <div className="flex items-center gap-2 text-zinc-900">
                <Database size={18} />
                <h2 className="text-lg font-semibold">Cache Management</h2>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-2 text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
              aria-label="Close cache management"
            >
              <X size={18} />
            </button>
          </div>

          <div className="min-h-0 overflow-y-auto p-6">
            <div className="grid gap-6 lg:grid-cols-[1.1fr,1.4fr]">
              <section className="rounded-xl border border-zinc-200 bg-zinc-50/60 p-4">
                <div className="mb-3">
                  <h3 className="text-sm font-semibold text-zinc-900">
                    Current Project Controls
                  </h3>
                  <p className="mt-1 text-xs text-zinc-500">
                    Project ID:{" "}
                    {currentProjectAvailable ? currentProjectId : "—"}
                  </p>
                </div>

                <div className="space-y-2">
                  {currentProjectLanguages.length > 0 ? (
                    currentProjectLanguages.map((language) => (
                      <button
                        key={`results:${language}`}
                        type="button"
                        disabled={!currentProjectAvailable}
                        onClick={() => {
                          if (!currentProjectAvailable) return;
                          if (
                            !window.confirm(
                              `Delete Results (${language.toUpperCase()})?`,
                            )
                          ) {
                            return;
                          }
                          clearSelectedCaches(currentProjectId as number, {
                            results: true,
                            languages: [language as OutputLanguage],
                          });
                          refresh();
                        }}
                        className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-left transition-colors hover:border-red-200 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-sm font-medium text-zinc-900">
                            Results ({language.toUpperCase()})
                          </span>
                          <Trash2 size={14} className="text-zinc-400" />
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="rounded-xl border border-dashed border-zinc-200 bg-white px-4 py-3 text-xs text-zinc-500">
                      No result cache for the current project.
                    </div>
                  )}

                  {[
                    {
                      label: "Project Data Sources",
                      disabled: !currentProjectEntry?.hasProjectDataSource,
                      onClick: () =>
                        clearSelectedCaches(currentProjectId as number, {
                          projectDataSources: true,
                        }),
                    },
                    {
                      label: "Merge Check Results",
                      disabled: !currentProjectEntry?.hasMergeCheckResult,
                      onClick: () =>
                        clearSelectedCaches(currentProjectId as number, {
                          mergeCheckResults: true,
                        }),
                    },
                    {
                      label: "Freeform Review Results",
                      disabled: !currentProjectEntry?.hasFreeformReviewResult,
                      onClick: () =>
                        clearSelectedCaches(currentProjectId as number, {
                          freeformReviewResults: true,
                        }),
                    },
                    {
                      label: "Contact Topics",
                      disabled: false,
                      onClick: () =>
                        clearSelectedCaches(currentProjectId as number, {
                          contactTopics: true,
                        }),
                    },
                    {
                      label: "Dismissed Checks",
                      disabled: false,
                      onClick: () =>
                        clearSelectedCaches(currentProjectId as number, {
                          dismissed: true,
                        }),
                    },
                  ].map((item) => (
                    <button
                      key={item.label}
                      type="button"
                      disabled={!currentProjectAvailable || item.disabled}
                      onClick={() => {
                        if (!currentProjectAvailable) return;
                        if (!window.confirm(`Delete ${item.label}?`)) return;
                        item.onClick();
                        refresh();
                      }}
                      className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-left transition-colors hover:border-red-200 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm font-medium text-zinc-900">
                          {item.label}
                        </span>
                        <Trash2 size={14} className="text-zinc-400" />
                      </div>
                    </button>
                  ))}
                </div>

                {recentEvictions.length > 0 && (
                  <div className="mt-5 border-t border-zinc-200 pt-4">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                      Recent Evictions
                    </h4>
                    <div className="mt-2 space-y-2">
                      {recentEvictions.slice(0, 5).map((entry) => (
                        <div
                          key={`${entry.projectId}:${entry.evictedAt}`}
                          className="rounded-lg border border-zinc-200 bg-white px-3 py-2"
                        >
                          <div className="text-sm font-medium text-zinc-800">
                            {entry.projectTitle}
                          </div>
                          <div className="mt-1 flex items-center justify-between text-xs text-zinc-500">
                            <span>ID {entry.projectId}</span>
                            <span>{formatBytes(entry.freedBytes)}</span>
                          </div>
                          <div className="mt-1 text-[11px] text-zinc-400">
                            {formatTimestamp(entry.evictedAt)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>

              <section className="rounded-xl border border-zinc-200 bg-white">
                <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
                  <div>
                    <h3 className="text-sm font-semibold text-zinc-900">
                      Cached Projects
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={refresh}
                    className="rounded-md border border-zinc-200 px-2.5 py-1 text-xs text-zinc-600 transition-colors hover:bg-zinc-50 hover:text-zinc-900"
                  >
                    Refresh
                  </button>
                </div>

                <div className="max-h-[60vh] overflow-y-auto">
                  {projectEntries.length === 0 ? (
                    <div className="px-4 py-10 text-center text-sm text-zinc-500">
                      No cached projects.
                    </div>
                  ) : (
                    <div className="divide-y divide-zinc-200">
                      {projectEntries.map((entry) => (
                        <div
                          key={entry.projectId}
                          className="grid gap-3 px-4 py-3 md:grid-cols-[minmax(0,1fr),auto]"
                        >
                          <div className="min-w-0">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="truncate text-sm font-medium text-zinc-900">
                                  {entry.projectTitle}
                                </div>
                                <div className="mt-1 text-xs text-zinc-500">
                                  Project ID: {entry.projectId}
                                </div>
                              </div>
                              <div className="shrink-0 rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] text-zinc-600">
                                {formatBytes(entry.totalBytes)}
                              </div>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
                              <span>
                                Updated: {formatTimestamp(entry.lastUpdatedAt)}
                              </span>
                              <span>
                                Languages:{" "}
                                {entry.languages.length > 0
                                  ? entry.languages.join(", ")
                                  : "—"}
                              </span>
                            </div>
                            {entry.sources.length > 0 && (
                              <div className="mt-2 space-y-1 text-xs text-zinc-500">
                                {entry.sources.slice(0, 3).map((source) => (
                                  <div
                                    key={source.sourceId}
                                    className="truncate"
                                    title={
                                      source.origin?.url ??
                                      source.origin?.fileName ??
                                      source.label
                                    }
                                  >
                                    Source: {source.label} ({source.kind})
                                  </div>
                                ))}
                                {entry.sources.length > 3 && (
                                  <div>
                                    +{entry.sources.length - 3} more sources
                                  </div>
                                )}
                              </div>
                            )}
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {entry.hasProjectDataSource && (
                                <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] text-violet-600">
                                  Source
                                </span>
                              )}
                              {entry.hasMergeCheckResult && (
                                <span className="rounded-full bg-cyan-50 px-2 py-0.5 text-[11px] text-cyan-600">
                                  Merge Check
                                </span>
                              )}
                              {entry.hasFreeformReviewResult && (
                                <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] text-indigo-600">
                                  Freeform Review
                                </span>
                              )}
                              {entry.hasResults && (
                                <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] text-blue-600">
                                  Results
                                </span>
                              )}
                              {entry.hasCheckpoint && (
                                <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-600">
                                  Checkpoint
                                </span>
                              )}
                              {entry.hasContactTopics && (
                                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-600">
                                  Contact Topics
                                </span>
                              )}
                              {entry.hasDismissed && (
                                <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] text-zinc-600">
                                  Dismissed
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-start justify-end">
                            <button
                              type="button"
                              onClick={() => {
                                if (
                                  !window.confirm(
                                    `Delete cache for project ${entry.projectId}?`,
                                  )
                                ) {
                                  return;
                                }
                                clearProjectCacheBundle(entry.projectId);
                                refresh();
                              }}
                              className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-sm text-red-600 transition-colors hover:bg-red-50"
                            >
                              <Trash2 size={14} />
                              Delete project cache
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            </div>

            {shareBundle && (
              <section className="mt-6 rounded-xl border border-zinc-200 bg-zinc-50/60 p-4">
                <h3 className="text-sm font-semibold text-zinc-900">
                  Share Bundle（デバッグ用）
                </h3>
                <p className="mt-1 text-xs text-zinc-500">
                  ブラウザキャッシュのチェック結果・旅程データを書き出し/取り込みします（デバッグ・移行用）。通常の結果共有は実行履歴（サーバ保存）を使ってください。
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={shareBundle.onDownload}
                    disabled={shareBundle.downloadDisabled}
                    className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Download size={14} />
                    Download Share Bundle
                  </button>
                  <button
                    type="button"
                    onClick={shareBundle.onImportClick}
                    className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 hover:text-blue-700"
                  >
                    <Upload size={14} />
                    Import Share Bundle
                  </button>
                  {shareBundle.status && (
                    <span className="text-xs font-medium text-gray-500">
                      {shareBundle.status}
                    </span>
                  )}
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
